/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */

import { jsx } from '@emotion/react'
import React, { Fragment, useEffect, useState, type FunctionComponent } from 'react'
import { useIntl } from 'react-intl'

import {
  EuiComboBox,
  type EuiComboBoxOptionOption,
  EuiFlexGroup,
  EuiFlexItem,
  EuiFormRow,
  EuiText,
} from '@elastic/eui'

import { type BudgetConfigurationValues } from '../../../../../types'

import useCloudResource from './useCloudResource'
import CloudResourceLoadingError from './CloudResourceLoadingError'

const deletedCloudResourceMessage = {
  id: 'billing.notifications.budgets.budget-settings.scope.cloud-resource.deleted-resource-warning',
  defaultMessage: 'Selected cloud resource has been removed. Select a different cloud resource.',
}

type Props = {
  cloudResourceRef: React.RefObject<HTMLDivElement>
  errors: Partial<Record<keyof BudgetConfigurationValues, string>>
  handleChange: (e: React.ChangeEvent<HTMLSelectElement>) => void
  isProcessing: boolean
  values: BudgetConfigurationValues
}

const CloudResource: FunctionComponent<Props> = ({
  cloudResourceRef,
  errors,
  handleChange,
  isProcessing,
  values,
}) => {
  const { formatMessage } = useIntl()
  const [isDisabled, setIsDisabled] = useState(false)

  const {
    cloudResourceOptions,
    hasDeletedCloudResource,
    isLoading,
    isLoadingError,
    refetch,
    selectedOptions,
  } = useCloudResource({
    values,
  })

  useEffect(() => {
    setIsDisabled(isProcessing || isLoading)
  }, [isProcessing, isLoading])

  const handleCloudResourceSelection = (selection: EuiComboBoxOptionOption[]) => {
    const selectedKeys = selection.map(({ key }) => key)
    const newScopeValues = [...new Set(selectedKeys)] // remove duplicates

    handleChange({
      target: {
        name: 'scopeValues' satisfies keyof BudgetConfigurationValues,
        value: newScopeValues,
      },
    } as unknown as React.ChangeEvent<HTMLSelectElement>)
  }

  const hasCloudResourceErrors = !!errors.scopeValues || hasDeletedCloudResource
  const errorMessage =
    errors.scopeValues || (hasDeletedCloudResource && formatMessage(deletedCloudResourceMessage))

  if (isLoadingError) {
    return <CloudResourceLoadingError isLoading={isLoading} refetch={refetch} />
  }

  return (
    <EuiFormRow
      label={formatMessage({
        id: 'billing.notifications.budgets.budget-settings.scope.cloud-resource.name.label',
        defaultMessage: 'Cloud resources',
      })}
      isDisabled={isDisabled}
      isInvalid={hasCloudResourceErrors}
      error={errorMessage}
    >
      <Fragment>
        <div
          data-test-id='billing.notifications.budgets.budget-settings.scope.cloud-resource.dropdown.focusable-ref'
          ref={cloudResourceRef}
        />
        <EuiComboBox
          aria-label={formatMessage({
            id: 'billing.notifications.budgets.budget-settings.scope.cloud-resource.dropdown.label',
            defaultMessage: 'Cloud resource selector',
          })}
          data-test-subj='billing.notifications.budgets.budget-settings.scope.cloud-resource.dropdown'
          isClearable={true}
          isDisabled={isDisabled}
          isLoading={isLoading}
          isInvalid={hasCloudResourceErrors}
          options={cloudResourceOptions}
          onChange={handleCloudResourceSelection}
          placeholder={formatMessage({
            id: 'billing.notifications.budgets.budget-settings.scope.cloud-resource.dropdown.placeholder',
            defaultMessage: 'Select a cloud resource',
          })}
          selectedOptions={selectedOptions}
          singleSelection={{ asPlainText: true }} // remove this line to enable multi-selection
          renderOption={(option: EuiComboBoxOptionOption<string>) => {
            const { key: id, label } = option
            const name = label.split('(')[0]

            return (
              <EuiFlexGroup direction='column' gutterSize='none'>
                <EuiFlexItem>
                  <EuiText size='s'>{name}</EuiText>
                </EuiFlexItem>
                <EuiFlexItem>
                  <EuiText size='xs' color='subdued'>
                    {id}
                  </EuiText>
                </EuiFlexItem>
              </EuiFlexGroup>
            )
          }}
          rowHeight='auto'
        />
      </Fragment>
    </EuiFormRow>
  )
}

export default CloudResource
